import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Apple, Download, Loader2, Plus } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import { useToast } from '@/stores/toast'
import { cn } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'
import type { Item } from '@/types/models'
import { Modal } from '@/components/Modal'
import { SearchBox } from '@/components/SearchBox'
import { Field, inputCls, selectCls } from '@/components/Field'
import { useCrudMutations } from '@/lib/useCrudMutations'
import { useListNavigation } from '@/lib/useListNavigation'
import { useFormFieldNavigation } from '@/lib/useFormFieldNavigation'

const ITEM_FORM_FIELDS = [
  'code', 'spec', 'name', 'procure_type', 'account_type',
  'unit_in', 'unit_out', 'unit_stock', 'invoice_print_name', 'memo',
] as const

interface ItemForm {
  id?: number
  code: string
  name: string; spec: string
  procure_type: string; account_type: string
  unit_in: string; unit_out: string; unit_stock: string
  invoice_print_name: string; memo: string
  is_active: boolean
}

const blankForm: ItemForm = {
  code: '', name: '', spec: '',
  procure_type: 'buy', account_type: 'product',
  unit_in: 'kg', unit_out: 'kg', unit_stock: 'kg',
  invoice_print_name: '', memo: '', is_active: true,
}

// 가상화 — 화면에 보이는 행 + 여유분만 DOM 에 올린다(수천 품목 OOM 방지).
const ROW_H = 38
const OVERSCAN = 10
const VIEWPORT_H = 620
const ITEM_COLS = [36, 96, 320, 112, 64, 96]  // 선택/코드/품명/규격/단위/작업

// 사용 = 출고·단가·매핑 검색에 나옴, 사용중지 = 검색에서 숨김(과거 출고·단가는 그대로)
type ActiveFilter = 'active' | 'inactive' | 'all'
const ACTIVE_PARAM: Record<ActiveFilter, string> = { active: '1', inactive: 'inactive', all: '0' }


export function ItemsTab() {
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('active')
  const [edit, setEdit] = useState<ItemForm | null>(null)
  const { downloading, download } = useFileDownload()

  const listParams = () => {
    const params = new URLSearchParams({ active: ACTIVE_PARAM[activeFilter] })
    if (search) params.set('q', search)
    return params
  }
  const { data: rows = [] } = useQuery({
    queryKey: ['items', search, activeFilter],
    queryFn: () => api<Item[]>(`/api/items?${listParams()}`),
  })

  // 여러 품목 체크 → 일괄 사용중지/사용(위하고 이관으로 생긴 안 쓰는 품목 정리, 고객 요청 2026-10-22)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // 검색어·필터가 바뀌면 선택 해제(안 보이는 품목이 선택된 채 남지 않게)
  const changeSearch = (v: string) => { setSearch(v); setSelected(new Set()); lastIdx.current = null }
  const changeFilter = (f: ActiveFilter) => { setActiveFilter(f); setSelected(new Set()); lastIdx.current = null }
  // Shift+클릭: 마지막으로 누른 줄부터 지금 줄까지 한 번에(지금 줄을 켜면 다 켜고, 끄면 다 끔)
  const lastIdx = useRef<number | null>(null)
  const toggle = (idx: number, shift: boolean) => {
    const on = !selected.has(rows[idx].id)
    const from = shift && lastIdx.current !== null ? Math.min(lastIdx.current, idx) : idx
    const to = shift && lastIdx.current !== null ? Math.max(lastIdx.current, idx) : idx
    setSelected((prev) => {
      const next = new Set(prev)
      for (const r of rows.slice(from, to + 1)) {
        if (on) next.add(r.id)
        else next.delete(r.id)
      }
      return next
    })
    lastIdx.current = idx
  }
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id))
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const bulkActive = useMutation({
    mutationFn: (isActive: boolean) => api<{ updated: number }>('/api/items/bulk-active', {
      method: 'POST', body: { ids: [...selected], is_active: isActive },
    }),
    onSuccess: (d, isActive) => {
      qc.invalidateQueries({ queryKey: ['items'] })
      setSelected(new Set())
      push(`${d.updated}개 품목 ${isActive ? '사용으로 바꿈' : '사용중지'}`, 'success')
    },
    onError: (e) => push(`변경 실패: ${e.message}`, 'error', 6000),
  })

  const { save, remove } = useCrudMutations<ItemForm, Item>({
    apiBase: '/api/items',
    queryKey: 'items',
    onSaved: () => setEdit(null),
  })

  async function downloadExcel() {
    await download(`/api/items/export?${listParams()}`, '품목관리.xlsx')
  }

  const { formRef, onKeyDown: onFormKeyDown } = useFormFieldNavigation(ITEM_FORM_FIELDS)

  // ── 가상화(windowing) ──
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(VIEWPORT_H)

  // 키보드 내비가 오프스크린 행으로 이동할 때 먼저 스크롤해 렌더시킨다.
  function ensureVisible(idx: number) {
    const el = scrollRef.current
    if (!el) return
    const top = idx * ROW_H
    const bot = top + ROW_H
    if (top < el.scrollTop) el.scrollTop = top
    else if (bot > el.scrollTop + el.clientHeight) el.scrollTop = bot - el.clientHeight
  }

  const { tbodyRef, onKeyDown: onListKeyDown } = useListNavigation<Item>({
    items: rows,
    disabled: !!edit,
    ensureVisible,
    onEnter: (r) => setEdit({ ...r }),
    onDelete: (r) => {
      void confirm(`'${r.name}'을 영구 삭제할까요? (거래 내역 있으면 삭제 안 됨)`, { danger: true }).then((ok) => ok && remove.mutate(r.id))
    },
  })

  useEffect(() => {
    const el = scrollRef.current
    if (el) { el.scrollTop = 0; setViewportH(el.clientHeight || VIEWPORT_H) }
    setScrollTop(0)
  }, [rows])

  function onListScroll() {
    const el = scrollRef.current
    if (!el) return
    setScrollTop(el.scrollTop)
    if (el.clientHeight && el.clientHeight !== viewportH) setViewportH(el.clientHeight)
  }

  const total = rows.length
  const startIdx = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const endIdx = Math.min(total, Math.ceil((scrollTop + viewportH) / ROW_H) + OVERSCAN)
  const visible = rows.slice(startIdx, endIdx)
  const topPad = startIdx * ROW_H
  const botPad = Math.max(0, (total - endIdx) * ROW_H)

  return (
    <div className="max-w-7xl mx-auto p-6">
      <header className="mb-5 flex items-center gap-3">
        <Apple className="w-6 h-6 text-emerald-600"/>
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight flex-1">품목관리</h2>
        <button onClick={() => { void downloadExcel() }}
                disabled={downloading}
                title="현재 검색 결과를 엑셀로 다운로드"
                className="bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5 disabled:opacity-50">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          엑셀 다운로드
        </button>
        <button onClick={() => setEdit({ ...blankForm })}
                data-role="add-btn"
                title="Alt+N"
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-4 py-2 inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4" /> 품목 추가
        </button>
      </header>

      <div className="sticky top-0 z-20 -mx-6 px-6 py-2 bg-slate-50/95 backdrop-blur border-b border-slate-200/60 mb-3">
        <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm">
          <SearchBox value={search} onChange={changeSearch} placeholder="품목코드, 품명, 원산지 검색" className="flex-1 min-w-[200px]" />
          <div className="inline-flex rounded-lg overflow-hidden border border-slate-300">
            {(['active', 'inactive', 'all'] as const).map((f) => (
              <button key={f} onClick={() => changeFilter(f)}
                      className={cn('px-3 py-2 text-xs font-semibold transition',
                        activeFilter === f
                          ? f === 'inactive' ? 'bg-slate-600 text-white' : 'bg-blue-600 text-white'
                          : 'bg-white text-slate-600 hover:bg-slate-50')}>
                {f === 'active' ? '사용' : f === 'inactive' ? '사용중지' : '전체'}
              </button>
            ))}
          </div>
          <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
          {selected.size > 0 && (
            <span className="inline-flex items-center gap-2 text-xs">
              <b className="text-slate-700">{selected.size}개 선택</b>
              <button onClick={() => bulkActive.mutate(false)} disabled={bulkActive.isPending}
                      className="px-2.5 py-1.5 rounded-md bg-slate-600 hover:bg-slate-700 text-white font-semibold disabled:opacity-50">
                사용중지
              </button>
              <button onClick={() => bulkActive.mutate(true)} disabled={bulkActive.isPending}
                      className="px-2.5 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold disabled:opacity-50">
                사용으로
              </button>
              <button onClick={() => setSelected(new Set())} className="text-slate-400 hover:text-slate-600">선택 해제</button>
            </span>
          )}
          <span className="text-[10px] text-slate-400">
            <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono">↑↓</kbd> 이동 ·
            <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Enter</kbd> 수정 ·
            <kbd className="px-1 py-0.5 bg-slate-100 border border-slate-300 rounded font-mono ml-1">Del</kbd> 삭제(거래 있으면 사용중지)
          </span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div ref={scrollRef} onScroll={onListScroll} className="overflow-auto" style={{ maxHeight: VIEWPORT_H }}>
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>{ITEM_COLS.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide sticky top-0 z-10">
            <tr>
              <th className="px-2 py-2.5 bg-slate-50">
                <input type="checkbox" checked={allSelected} disabled={!rows.length}
                       title="목록 전체 선택"
                       onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
                       className="accent-slate-600 w-4 h-4 align-middle"/>
              </th>
              <th className="px-3 py-2.5 text-left font-medium bg-slate-50">코드</th>
              <th className="px-3 py-2.5 text-left font-medium bg-slate-50">품명</th>
              <th className="px-3 py-2.5 text-left font-medium bg-slate-50">규격(원산지)</th>
              <th className="px-3 py-2.5 text-left font-medium bg-slate-50">단위</th>
              <th className="px-3 py-2.5 text-right font-medium bg-slate-50"></th>
            </tr>
          </thead>
          <tbody ref={tbodyRef} onKeyDown={onListKeyDown}>
            {topPad > 0 && <tr aria-hidden style={{ height: topPad }}><td colSpan={6} className="p-0"/></tr>}
            {visible.map((r, i) => {
              const idx = startIdx + i
              return (
              <tr key={r.id} data-idx={idx} tabIndex={idx === 0 ? 0 : -1} style={{ height: ROW_H }}
                  className={cn('border-t border-slate-100 focus:bg-blue-50 focus:outline-none',
                    r.is_active ? 'hover:bg-slate-50' : 'bg-slate-50/60 text-slate-400 hover:bg-slate-100/60')}>
                <td className="px-2 py-2">
                  <input type="checkbox" checked={selected.has(r.id)} readOnly
                         title="Shift+클릭: 앞에 누른 줄까지 한 번에"
                         onMouseDown={(e) => { if (e.shiftKey) e.preventDefault() }}   // Shift+클릭 글자 선택 방지
                         onClick={(e) => toggle(idx, e.shiftKey)}
                         className="accent-slate-600 w-4 h-4 align-middle"/>
                </td>
                <td className="px-3 py-2 font-mono text-xs text-slate-600 tabular-nums truncate">{r.code}</td>
                <td className={cn('px-3 py-2 font-medium truncate', r.is_active ? 'text-slate-900' : 'text-slate-400')} title={r.name}>
                  {!r.is_active && (
                    <span className="mr-1.5 text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-semibold">사용중지</span>
                  )}
                  {r.name}
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs truncate" title={r.spec}>{r.spec}</td>
                <td className="px-3 py-2 text-slate-500 text-xs truncate">{r.unit_out}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap overflow-hidden">
                  <button onClick={() => setEdit({ ...r })}
                          className="text-blue-600 hover:text-blue-800 text-xs mr-2">수정</button>
                  <button onClick={() => { void confirm(`'${r.name}'을 영구 삭제할까요? (거래 내역 있으면 삭제 안 됨)`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700 text-xs">삭제</button>
                </td>
              </tr>
              )
            })}
            {botPad > 0 && <tr aria-hidden style={{ height: botPad }}><td colSpan={6} className="p-0"/></tr>}
            {!rows.length && (
              <tr><td colSpan={6} className="text-center text-slate-400 py-10 text-sm">
                품목이 없습니다. 우측 상단 "품목 추가"로 시작하세요.
              </td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} title={edit?.id ? '품목 수정' : '품목 추가'} size="xl">
        {edit && (
          <form ref={formRef} onKeyDown={onFormKeyDown}
                onSubmit={(e) => { e.preventDefault(); save.mutate(edit) }}
                className="grid grid-cols-2 gap-3">
            <Field label="품목코드">
              {edit.id ? (
                <input value={edit.code} readOnly tabIndex={-1}
                       className={inputCls + ' tabular-nums bg-slate-50 text-slate-500 cursor-not-allowed'}/>
              ) : (
                <input data-form-field="code" value={edit.code} onChange={(e) => setEdit({ ...edit, code: e.target.value })}
                       placeholder="빈칸이면 자동 채번"
                       className={inputCls + ' tabular-nums'}/>
              )}
            </Field>
            <Field label="규격 (원산지)"><input data-form-field="spec" value={edit.spec} onChange={(e) => setEdit({ ...edit, spec: e.target.value })} placeholder="국내산 / 수입산 / 중국산" className={inputCls}/></Field>
            <Field label="품명 *" full><input data-form-field="name" autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required className={inputCls}/></Field>
            <Field label="조달구분">
              <select data-form-field="procure_type" value={edit.procure_type} onChange={(e) => setEdit({ ...edit, procure_type: e.target.value })} className={selectCls}>
                <option value="buy">구매품</option>
                <option value="make">생산품</option>
                <option value="subcontract">외주</option>
              </select>
            </Field>
            <Field label="품목계정">
              <select data-form-field="account_type" value={edit.account_type} onChange={(e) => setEdit({ ...edit, account_type: e.target.value })} className={selectCls}>
                <option value="product">상품</option>
                <option value="material">원재료</option>
                <option value="sub_material">부재료</option>
                <option value="manufactured">제품</option>
                <option value="semi">반제품</option>
                <option value="byproduct">부산품</option>
                <option value="storage">저장품</option>
              </select>
            </Field>
            <Field label="입고단위"><input data-form-field="unit_in" value={edit.unit_in} onChange={(e) => setEdit({ ...edit, unit_in: e.target.value })} className={inputCls}/></Field>
            <Field label="출고단위"><input data-form-field="unit_out" value={edit.unit_out} onChange={(e) => setEdit({ ...edit, unit_out: e.target.value })} className={inputCls}/></Field>
            <Field label="재고단위"><input data-form-field="unit_stock" value={edit.unit_stock} onChange={(e) => setEdit({ ...edit, unit_stock: e.target.value })} className={inputCls}/></Field>
            <Field label="거래명세서 출력명" full><input data-form-field="invoice_print_name" value={edit.invoice_print_name} onChange={(e) => setEdit({ ...edit, invoice_print_name: e.target.value })} className={inputCls}/></Field>
            <Field label="비고" full><textarea data-form-field="memo" value={edit.memo} onChange={(e) => setEdit({ ...edit, memo: e.target.value })} rows={2} className={inputCls}/></Field>
            <Field label="사용중지" full>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" checked={!edit.is_active}
                       onChange={(e) => setEdit({ ...edit, is_active: !e.target.checked })}
                       className="accent-slate-600 w-4 h-4"/>
                <span>더 쓰지 않는 품목 — 출고·단가·매핑 검색에 안 나옴 (과거 출고·단가는 그대로)</span>
              </label>
            </Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg">저장</button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
